/**
 * Lexical retrieval over the Ask catalog: a small BM25 over title, keywords
 * and body (weighted), language-blind (EN text and every translation are
 * indexed together). Pure; runs in the browser.
 */
import type { AskEntry } from "./catalog";

const FIELD_WEIGHT = { title: 3, keywords: 2, body: 1 } as const;
const K1 = 1.2;
const B = 0.75;
/** Below this score nothing is worth showing. */
export const MIN_SCORE = 1.5;
/** Results weaker than this share of the best one are dropped. */
const RELATIVE_CUTOFF = 0.35;
const MAX_RESULTS = 5;

const STOPWORDS = new Set(
  "a an the to of for in on at by with and or is are be do does i we you my our how where what when can which it this that from into".split(
    " "
  )
);

function stem(word: string): string {
  if (word.length > 5 && word.endsWith("ing")) return word.slice(0, -3);
  if (word.length > 4 && word.endsWith("ed")) return word.slice(0, -2);
  if (word.length > 4 && word.endsWith("es")) return word.slice(0, -2);
  if (word.length > 3 && word.endsWith("s") && !word.endsWith("ss")) return word.slice(0, -1);
  return word;
}

export function tokenize(text: string): string[] {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length > 1 && !STOPWORDS.has(w))
    .map(stem);
}

interface Doc {
  entry: AskEntry;
  fields: Record<keyof typeof FIELD_WEIGHT, string[]>;
}

export interface AskIndex {
  docs: Doc[];
  df: Map<string, number>;
  avgLen: Record<keyof typeof FIELD_WEIGHT, number>;
}

export function buildIndex(entries: readonly AskEntry[]): AskIndex {
  const docs: Doc[] = entries.map((entry) => {
    const tr = Object.values(entry.i18n ?? {});
    return {
      entry,
      fields: {
        title: tokenize([entry.title, ...tr.map((t) => t?.title ?? "")].join(" ")),
        keywords: tokenize((entry.keywords ?? []).join(" ")),
        body: tokenize([entry.body, entry.needs ?? "", ...tr.map((t) => `${t?.body ?? ""} ${t?.needs ?? ""}`)].join(" ")),
      },
    };
  });
  const df = new Map<string, number>();
  for (const doc of docs) {
    const seen = new Set([...doc.fields.title, ...doc.fields.keywords, ...doc.fields.body]);
    seen.forEach((term) => df.set(term, (df.get(term) ?? 0) + 1));
  }
  const avg = (f: keyof typeof FIELD_WEIGHT) =>
    docs.reduce((n, d) => n + d.fields[f].length, 0) / Math.max(1, docs.length) || 1;
  return { docs, df, avgLen: { title: avg("title"), keywords: avg("keywords"), body: avg("body") } };
}

export interface AskHit {
  entry: AskEntry;
  score: number;
}

export function searchCatalog(index: AskIndex, query: string): AskHit[] {
  const terms = Array.from(new Set(tokenize(query)));
  if (terms.length === 0) return [];
  const n = index.docs.length;
  const hits: AskHit[] = [];
  for (const doc of index.docs) {
    let score = 0;
    for (const term of terms) {
      const df = index.df.get(term);
      if (!df) continue;
      const idf = Math.log(1 + (n - df + 0.5) / (df + 0.5));
      for (const field of Object.keys(FIELD_WEIGHT) as (keyof typeof FIELD_WEIGHT)[]) {
        const tokens = doc.fields[field];
        const tf = tokens.filter((t) => t === term).length;
        if (!tf) continue;
        const norm = tf * (K1 + 1) / (tf + K1 * (1 - B + (B * tokens.length) / index.avgLen[field]));
        score += FIELD_WEIGHT[field] * idf * norm;
      }
    }
    if (score > 0) hits.push({ entry: doc.entry, score });
  }
  hits.sort((a, b) => b.score - a.score);
  const best = hits[0]?.score ?? 0;
  if (best < MIN_SCORE) return [];
  return hits.filter((h) => h.score >= best * RELATIVE_CUTOFF).slice(0, MAX_RESULTS);
}
